import { useEffect, useMemo } from 'react';

import { analyticsProps } from '@class-ui/insights';
import { Popper, Grow, Paper, MenuItem, ListItemIcon, ListItemText, ClickAwayListener } from '@mui/material';

import { type ActionOnClick, type ActionMenuItem } from '../table/ActionMenu';

type Position = { top: number; left: number; height?: number };

type Props = {
    anchorPos: Position | null;
    actions: ActionMenuItem[];
    onClose: () => void;
    analyticsId?: string;
    actionId?: string; // The action for the current thing you want to act uppon, e.g. a recording Id.
};

export const PositionableMenu = ({ anchorPos, actions, onClose, actionId, analyticsId }: Props) => {
    const open = Boolean(anchorPos);
    // Prevent scroll while menu is open
    useEffect(() => {
        if (open) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [open]);

    // Create virtual element for Popper positioning
    const virtualAnchor = useMemo(() => {
        if (!anchorPos) return null;
        const top = anchorPos.top + (anchorPos.height ?? 0);
        const left = anchorPos.left;
        return {
            getBoundingClientRect: () =>
                ({
                    top,
                    left,
                    bottom: top,
                    right: left,
                    width: 0,
                    height: 0,
                    x: left,
                    y: top,
                    toJSON: () => {}
                }) as DOMRect
        };
    }, [anchorPos]);

    if (!virtualAnchor) return null;

    const handleAction = (action: ActionOnClick, keepOpen = false) => {
        if (!keepOpen) {
            onClose();
        }

        if (actionId) {
            action(actionId);
        }
    };

    return (
        <Popper
            open
            anchorEl={virtualAnchor}
            placement="bottom-start"
            transition
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8]
                    }
                }
            ]}
        >
            {({ TransitionProps }) => (
                <ClickAwayListener onClickAway={onClose}>
                    <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                        <Paper
                            elevation={8}
                            sx={{
                                borderRadius: 1,
                                mt: 1,
                                minWidth: 160
                            }}
                            {...analyticsProps(analyticsId ?? 'positionable-menu')}
                        >
                            {actions.map((action) => (
                                <MenuItem
                                    key={`action-${action.name}`}
                                    onClick={() => handleAction(action.onClick, action.keepOpen ?? false)}
                                >
                                    {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
                                    <ListItemText>{action.name}</ListItemText>
                                </MenuItem>
                            ))}
                        </Paper>
                    </Grow>
                </ClickAwayListener>
            )}
        </Popper>
    );
};
